import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { app } from "../../firebase";
import NavbarDashboard from "../Dashboard/NavbarDashboard";
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Grid,
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import ResponsiveDrawer from "../Dashboard/Sidebar";
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const firestore = getFirestore(app);
const DEFAULT_TDS_STATE = {
  bankconfirm: false,
  enableOtp: true,
  fileTds: false,
  finalOtp: false,
  finalotpstatus: false,
  incomesiteregistration: false,
  paymentstatus: false,
  processing: false,
  registraionOtp: false,
  showOtp: true,
  tdsA26s: false,
  tdsDetails: false,
  tdsformfilluped: true,
};

const UserDetails = ({ userPermissions }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Edit dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [updating, setUpdating] = useState(false);

  // Pagination states
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Snackbar states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Cache for fetched data
  const [cache, setCache] = useState({
    users: null,
    lastFetch: 0,
  });

  // Ref for table container to scroll smoothly
  const tableContainerRef = useRef(null);
  const searchBarRef = useRef(null);

  const scrollToTableTop = useCallback(() => {
    if (!tableContainerRef.current) return;

    const headerOffset = 104;
    const tableTop =
      tableContainerRef.current.getBoundingClientRect().top +
      window.pageYOffset -
      headerOffset;

    window.scrollTo({
      top: Math.max(tableTop, 0),
      behavior: "smooth",
    });
  }, []);

  const fetchData = useCallback(
    async (forceRefresh = false) => {
      const now = Date.now();
      if (
        !forceRefresh &&
        cache.lastFetch &&
        now - cache.lastFetch < 120000 &&
        cache.users
      ) {
        setUsers(cache.users);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const [usersSnapshot, tdsFormSnapshot, tdsStateSnapshot] =
          await Promise.all([
            getDocs(collection(firestore, "users")),
            getDocs(collection(firestore, "tds_forms")),
            getDocs(collection(firestore, "tds_state")),
          ]);

        const usersData = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const tdsFormMap = new Map(
          tdsFormSnapshot.docs.map((formDoc) => {
            const data = formDoc.data();
            return [
              data.userId ?? formDoc.id,
              {
                id: formDoc.id,
                ...data,
                timestamp: data.timestamp?.toDate() ?? null,
              },
            ];
          }),
        );
        const tdsStateMap = new Map(
          tdsStateSnapshot.docs.map((stateDoc) => [
            stateDoc.id,
            stateDoc.data(),
          ]),
        );

        const mergedData = usersData.map((user) => {
          const tdsForm = tdsFormMap.get(user.id) || {};
          const tdsState = tdsStateMap.get(user.id) || {};
          
          // Omit email/phone from tdsForm to prevent overwrite of registration credentials
          const { email: formEmail, phone: formPhone, ...restTdsForm } = tdsForm;
          
          return { ...user, ...restTdsForm, tdsState };
        });

        const sortedData = mergedData.sort((a, b) => {
          const timeA = a.createdAt
            ? a.createdAt.seconds
              ? a.createdAt.seconds * 1000
              : new Date(a.createdAt).getTime()
            : a.timestamp
              ? new Date(a.timestamp).getTime()
              : 0;

          const timeB = b.createdAt
            ? b.createdAt.seconds
              ? b.createdAt.seconds * 1000
              : new Date(b.createdAt).getTime()
            : b.timestamp
              ? new Date(b.timestamp).getTime()
              : 0;

          return timeB - timeA;
        });

        setCache({
          users: sortedData,
          lastFetch: now,
        });

        setUsers(sortedData);
      } catch (error) {
        console.error("Error fetching data:", error);
        setSnackbar({
          open: true,
          message: "Error fetching user data",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [cache.lastFetch, cache.users],
  );

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), 300000); // 5 minutes
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    if (loading) return;
    if (!searchTerm && !startDate && !endDate) return;

    const timer = setTimeout(() => {
      scrollToTableTop();
    }, 150);

    return () => clearTimeout(timer);
  }, [searchTerm, startDate, endDate, loading, scrollToTableTop]);

  // Filtered users with memoization
  const filteredUsers = useMemo(() => {
    let result = [...users];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (user) =>
          user.name?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          (user.phone && user.phone.includes(searchTerm)) ||
          user.pan?.toLowerCase().includes(searchLower),
      );
    }

    if (startDate || endDate) {
      result = result.filter((user) => {
        const userDate = user.timestamp ? new Date(user.timestamp) : null;
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;

        start?.setHours(0, 0, 0, 0);
        end?.setHours(23, 59, 59, 999);

        return (
          (!start || (userDate && userDate >= start)) &&
          (!end || (userDate && userDate <= end))
        );
      });
    }

    return result;
  }, [users, searchTerm, startDate, endDate]);

  // Pagination
  const paginatedUsers = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredUsers.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredUsers, page, rowsPerPage]);

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      dob: user.dob || "",
      pan: user.pan || "",
      address: user.address || "",
      pin_code: user.pin_code || "",
      bank_account: user.bank_account || "",
      bank_ifsc: user.bank_ifsc || "",
      aadhar: user.aadhar || "",
    });
    setEditDialogOpen(true);
  };

  const handleEditFieldChange = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;

    setUpdating(true);
    try {
      const tdsFormRef = doc(firestore, "tds_forms", selectedUser.id);
      const tdsStateRef = doc(firestore, "tds_state", selectedUser.id);

      const tdsFormPayload = {
        ...editFormData,
        userId: selectedUser.id,
        timestamp: serverTimestamp(),
      };

      await Promise.all([
        setDoc(tdsFormRef, tdsFormPayload, { merge: true }),
        setDoc(
          tdsStateRef,
          {
            ...DEFAULT_TDS_STATE,
            ...(selectedUser.tdsState || {}),
            tdsformfilluped: true,
          },
          { merge: true },
        ),
      ]);

      // Update local state
      setUsers((prevUsers) => {
        const updatedUsers = prevUsers.map((user) =>
          user.id === selectedUser.id
            ? {
                ...user,
                ...tdsFormPayload,
                tdsState: {
                  ...DEFAULT_TDS_STATE,
                  ...(user.tdsState || {}),
                  tdsformfilluped: true,
                },
              }
            : user,
        );

        setCache({
          users: updatedUsers,
          lastFetch: Date.now(),
        });

        return updatedUsers;
      });

      setSnackbar({
        open: true,
        message: "User details updated successfully!",
        severity: "success",
      });

      setEditDialogOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error("Error updating user:", error);
      setSnackbar({
        open: true,
        message: "Failed to update user details",
        severity: "error",
      });
    } finally {
      setUpdating(false);
    }
  };

  const downloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredUsers.map((user, index) => ({
        "Sr No.": index + 1,
        "Date & Time": user.timestamp
          ? new Date(user.timestamp).toLocaleString()
          : "NA",
        "User Name": user.name || "",
        "Email ID": user.email || "",
        "Phone No.": user.phone || "",
        "First Name": user.first_name || "",
        "Last Name": user.last_name || "",
        DOB: user.dob || "",
        PAN: user.pan || "",
        Address: user.address || "",
        Pincode: user.pin_code || "",
        "Bank A/C": user.bank_account || "",
        IFSC: user.bank_ifsc || "",
        Aadhar: user.aadhar || "",
      })),
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "User Details");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(
      dataBlob,
      `user_details_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    scrollToTableTop();
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
    scrollToTableTop();
  };

  const formatDate = (user) => {
    if (user.createdAt?.seconds) {
      return new Date(
        user.createdAt.seconds * 1000 +
          (user.createdAt.nanoseconds || 0) / 1000000,
      ).toLocaleString();
    }
    if (user.timestamp) {
      return new Date(user.timestamp).toLocaleString();
    }
    return "NA";
  };

  const LoadingSkeleton = () => (
    <>
      {[...Array(rowsPerPage)].map((_, i) => (
        <tr key={i}>
          <td>
            <div
              className="skeleton"
              style={{ height: "20px", width: "40px" }}
            ></div>
          </td>
          <td>
            <div
              className="skeleton"
              style={{ height: "20px", width: "150px" }}
            ></div>
          </td>
          <td>
            <div
              className="skeleton"
              style={{ height: "20px", width: "120px" }}
            ></div>
          </td>
          <td>
            <div
              className="skeleton"
              style={{ height: "20px", width: "180px" }}
            ></div>
          </td>
          <td>
            <div
              className="skeleton"
              style={{ height: "20px", width: "100px" }}
            ></div>
          </td>
          <td>
            <div
              className="skeleton"
              style={{ height: "20px", width: "80px" }}
            ></div>
          </td>
          <td>
            <div
              className="skeleton"
              style={{ height: "20px", width: "80px" }}
            ></div>
          </td>
          <td>
            <div
              className="skeleton"
              style={{ height: "20px", width: "80px" }}
            ></div>
          </td>
          <td>
            <div
              className="skeleton"
              style={{ height: "20px", width: "100px" }}
            ></div>
          </td>
          <td>
            <div
              className="skeleton"
              style={{ height: "20px", width: "150px" }}
            ></div>
          </td>
          <td>
            <div
              className="skeleton"
              style={{ height: "20px", width: "60px" }}
            ></div>
          </td>
          <td>
            <div
              className="skeleton"
              style={{ height: "20px", width: "100px" }}
            ></div>
          </td>
          <td>
            <div
              className="skeleton"
              style={{ height: "20px", width: "60px" }}
            ></div>
          </td>
          <td>
            <div
              className="skeleton"
              style={{ height: "30px", width: "80px" }}
            ></div>
          </td>
        </tr>
      ))}
    </>
  );

  return (
    <div>
      <NavbarDashboard />
      <Box height={10} />
      <Box sx={{ display: "flex" }}>
        <ResponsiveDrawer userPermissions={userPermissions} />
        <Box component="main" sx={{ flexGrow: 1, p: 3, pt: 8 }}>
          <div className="container">
            {/* Search and Filter Bar - Added ref */}
            <div ref={searchBarRef}>
              <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by name, email, phone or PAN..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setPage(1);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Start Date"
                    InputLabelProps={{ shrink: true }}
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setPage(1);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="End Date"
                    InputLabelProps={{ shrink: true }}
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setPage(1);
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => fetchData(true)}
                    startIcon={<RefreshIcon />}
                    sx={{ bgcolor: "#4caf50" }}
                  >
                    Refresh
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={downloadExcel}
                    startIcon={<DownloadIcon />}
                    sx={{ bgcolor: "#2196f3" }}
                  >
                    Download Excel
                  </Button>
                </Grid>
              </Grid>
            </div>

            {/* Stats Chips */}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              <Chip
                label={`Total Users: ${filteredUsers.length}`}
                color="primary"
                variant="filled"
              />
              <Chip
                label={`Active Users: ${users.filter((u) => u.tdsState?.fileTds).length}`}
                color="success"
                variant="outlined"
              />
            </Box>

            {/* User Table */}
            <div className="table-wrapper" ref={tableContainerRef}>
              <table className="responsive-table">
                <thead>
                  <tr>
                    <th className="sticky-header">#</th>
                    <th className="sticky-header">Date & Time</th>
                    <th className="sticky-header sticky-column">User Name</th>
                    <th className="sticky-header">Email ID</th>
                    <th className="sticky-header">Phone No.</th>
                    <th className="sticky-header">First Name</th>
                    <th className="sticky-header">Last Name</th>
                    <th className="sticky-header">DOB</th>
                    <th className="sticky-header">PAN</th>
                    <th className="sticky-header">Address</th>
                    <th className="sticky-header">Pincode</th>
                    <th className="sticky-header">Bank A/C</th>
                    <th className="sticky-header">IFSC</th>
                    <th className="sticky-header">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <LoadingSkeleton />
                  ) : (
                    paginatedUsers.map((user, index) => (
                      <tr
                        key={user.id}
                        className={index % 2 === 0 ? "even" : "odd"}
                      >
                        <td>{(page - 1) * rowsPerPage + index + 1}</td>
                        <td>{formatDate(user)}</td>
                        <td className="sticky-column">{user.name || "NA"}</td>
                        <td>{user.email || "NA"}</td>
                        <td>{user.phone || "NA"}</td>
                        <td>{user.first_name || "NA"}</td>
                        <td>{user.last_name || "NA"}</td>
                        <td>{user.dob || "NA"}</td>
                        <td>{user.pan || "NA"}</td>
                        <td>{user.address || "NA"}</td>
                        <td>{user.pin_code || "NA"}</td>
                        <td>{user.bank_account || "NA"}</td>
                        <td>{user.bank_ifsc || "NA"}</td>
                        <td>
                          <Tooltip title="Edit User">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(user)}
                              sx={{ color: "#2196f3" }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loading && filteredUsers.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mt: 3,
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Pagination
                  count={Math.ceil(filteredUsers.length / rowsPerPage)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                  >
                    <MenuItem value={10}>10 / page</MenuItem>
                    <MenuItem value={25}>25 / page</MenuItem>
                    <MenuItem value={50}>50 / page</MenuItem>
                    <MenuItem value={100}>100 / page</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="textSecondary">
                  Showing {(page - 1) * rowsPerPage + 1} to{" "}
                  {Math.min(page * rowsPerPage, filteredUsers.length)} of{" "}
                  {filteredUsers.length}
                </Typography>
              </Box>
            )}

            {/* No Results */}
            {!loading && filteredUsers.length === 0 && (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="h6" color="textSecondary">
                  No users found matching the criteria
                </Typography>
              </Box>
            )}
          </div>
        </Box>
      </Box>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit User Details - {selectedUser?.name}
          <IconButton
            aria-label="close"
            onClick={() => setEditDialogOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={editFormData.name || ""}
                onChange={(e) => handleEditFieldChange("name", e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={editFormData.email || ""}
                onChange={(e) => handleEditFieldChange("email", e.target.value)}
                size="small"
                type="email"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={editFormData.phone || ""}
                onChange={(e) => handleEditFieldChange("phone", e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={editFormData.first_name || ""}
                onChange={(e) =>
                  handleEditFieldChange("first_name", e.target.value)
                }
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={editFormData.last_name || ""}
                onChange={(e) =>
                  handleEditFieldChange("last_name", e.target.value)
                }
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                value={editFormData.dob || ""}
                onChange={(e) => handleEditFieldChange("dob", e.target.value)}
                size="small"
                placeholder="DD/MM/YYYY"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PAN Number"
                value={editFormData.pan || ""}
                onChange={(e) => handleEditFieldChange("pan", e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Aadhar Number"
                value={editFormData.aadhar || ""}
                onChange={(e) =>
                  handleEditFieldChange("aadhar", e.target.value)
                }
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={editFormData.address || ""}
                onChange={(e) =>
                  handleEditFieldChange("address", e.target.value)
                }
                size="small"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Pincode"
                value={editFormData.pin_code || ""}
                onChange={(e) =>
                  handleEditFieldChange("pin_code", e.target.value)
                }
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank Account Number"
                value={editFormData.bank_account || ""}
                onChange={(e) =>
                  handleEditFieldChange("bank_account", e.target.value)
                }
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank IFSC Code"
                value={editFormData.bank_ifsc || ""}
                onChange={(e) =>
                  handleEditFieldChange("bank_ifsc", e.target.value)
                }
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSaveUser}
            color="primary"
            variant="contained"
            disabled={updating}
            startIcon={updating ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {updating ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default UserDetails;

// CSS styles (same as before)
const css = `
.even {
  background-color: #f2f2f2;
}
.odd {
  background-color: #ffffff;
}

.sticky-header {
  position: sticky;
  top: 0;
  background-color: #90EE90 !important;
  z-index: 100;
  box-shadow: 0 2px 4px rgba(0,0,0,0.12);
  background-clip: padding-box;
}

.sticky-header:not(.sticky-column) {
  z-index: 110;
}

.table-wrapper {
  overflow-x: auto;
  max-height: calc(100vh - 280px);
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  position: relative;
  scroll-margin-top: 24px;
}

.responsive-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1400px;
}

.responsive-table th,
.responsive-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
  font-size: 13px;
}

.responsive-table th {
  font-weight: 600;
  background-color: #90EE90;
}

.sticky-column {
  position: sticky;
  left: 0;
  background-color: inherit;
  z-index: 105;
}

.sticky-header.sticky-column {
  background-color: #90EE90 !important;
  z-index: 115;
}

.skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

tr:hover {
  background-color: #f5f5f5 !important;
}
`;

document.head.insertAdjacentHTML("beforeend", `<style>${css}</style>`);
